// Main Capitolato Form Page with Wizard Interface, Dynamic Flow Rules, and Multi-Configuration Support
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Header, Button, LoadingSpinner } from '../../layout';
import { setDocumentTitle } from '../../utils/titleUtils';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';
import {
    HeaderSection,
    NinfaModelSection,
    CategorySection,
    FinishSection,
    GlassSection,
    FixingSection,
    DrainageHolesSection,
    MillingSection,
    PlatesSection,
    PriceSection,
    ImageSelectionSection,
    ReviewSection
} from './FormSections';
import ConfigurationList from './ConfigurationList';
import {
    generateCapitolatoPDF,
    previewCapitolatoPDF,
    generateGroupedCapitolatoPDF,
    previewGroupedCapitolatoPDF
} from '../../services/capitolatoPdfGenerator';
import { getAvailableOptions } from '../../utils/ninfaFlowRules';
import Swal from 'sweetalert2';

class StepErrorBoundary extends React.Component {
    constructor(props) { super(props); this.state = { error: null }; }
    static getDerivedStateFromError(error) { return { error }; }
    componentDidCatch(error, info) { console.error('Capitolato step crash:', error, info); }
    componentDidUpdate(prevProps) { if (prevProps.stepKey !== this.props.stepKey && this.state.error) this.setState({ error: null }); }
    render() {
        if (this.state.error) {
            return (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <p className="font-semibold mb-1">Errore nel render dello step.</p>
                    <p className="font-mono text-xs break-all">{String(this.state.error?.message || this.state.error)}</p>
                </div>
            );
        }
        return this.props.children;
    }
}

const TOTAL_STEPS = 12;

const CONTACT_FIELDS = ['nominativi', 'indirizzo', 'telefono', 'email', 'rifCantiere'];

const DEFAULT_PRODUCT_DATA = {
    modelloNinfa: '',
    categoria: '',
    finituraAlluminio: '',
    ralCustomCode: '',
    spessoreVetro: '',
    tipoFissaggio: '',
    foriScarico: '',
    fresaturaScarico: '',
    piattiScarico: '',
    prezzo: '',
    quantitaStimata: '',
    validitaOfferta: '',
    note: '',
    sezioneTecnicaImages: [],
    selectedEndPageImages: []
};

const DEFAULT_FORM_DATA = {
    nominativi: '',
    indirizzo: '',
    telefono: '',
    email: '',
    rifCantiere: '',
    ...DEFAULT_PRODUCT_DATA
};

const extractContactInfo = (data) => {
    const contact = {};
    CONTACT_FIELDS.forEach(f => { contact[f] = data[f] || ''; });
    return contact;
};

const extractProductConfig = (data) => {
    const config = {};
    Object.keys(DEFAULT_PRODUCT_DATA).forEach(f => { config[f] = data[f] ?? DEFAULT_PRODUCT_DATA[f]; });
    return config;
};

const CapitolatoForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    const revisionData = location.state?.revisionData || null;
    const isRevisionMode = !!revisionData;

    // Multi-config state
    const [configurations, setConfigurations] = useState([]);
    const [activeConfigIndex, setActiveConfigIndex] = useState(null);
    // Ref to hold heavy image data per config (not in state to avoid re-renders)
    const configImagesRef = useRef([]);
    // Skip the model-change reset when loading a full config
    const skipModelResetRef = useRef(false);

    const [currentStep, setCurrentStep] = useState(1);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showConfirmGenerate, setShowConfirmGenerate] = useState(false);
    const [formData, setFormData] = useState(() => {
        if (revisionData?.form_data) {
            const fd = revisionData.form_data;
            if (fd.isGrouped) {
                // Grouped revision: load first config into formData, rest into configurations
                const contactInfo = fd.contactInfo || {};
                const configs = fd.configurations || [];
                if (configs.length > 0) {
                    // Pre-load all configs except first into configurations state
                    // (done in useEffect below to avoid setState during init)
                    return {
                        ...DEFAULT_FORM_DATA,
                        ...contactInfo,
                        ...configs[0],
                        sezioneTecnicaImages: []
                    };
                }
                return { ...DEFAULT_FORM_DATA, ...contactInfo, sezioneTecnicaImages: [] };
            }
            // Legacy single-config revision
            return {
                ...DEFAULT_FORM_DATA,
                ...fd,
                sezioneTecnicaImages: []
            };
        }
        return { ...DEFAULT_FORM_DATA };
    });

    // Skip model reset on mount when loading revision data
    useEffect(() => {
        if (revisionData?.form_data) {
            skipModelResetRef.current = true;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Initialize grouped revision configs after mount
    useEffect(() => {
        if (revisionData?.form_data?.isGrouped) {
            const configs = revisionData.form_data.configurations || [];
            if (configs.length > 1) {
                const otherConfigs = configs.slice(1).map(c => ({
                    ...c,
                    sezioneTecnicaImages: []
                }));
                setConfigurations(otherConfigs);
                configImagesRef.current = otherConfigs.map(() => []);
            }
            setActiveConfigIndex(0);
            setCurrentStep(2);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setDocumentTitle('Capitolato - Generatore Offerte');
    }, []);

    useEffect(() => {
        if (skipModelResetRef.current) {
            skipModelResetRef.current = false;
            return;
        }
        if (formData.modelloNinfa) {
            setFormData(prev => ({
                ...prev,
                categoria: '',
                finituraAlluminio: '',
                ralCustomCode: '',
                spessoreVetro: '',
                tipoFissaggio: '',
                foriScarico: '',
                fresaturaScarico: '',
                piattiScarico: ''
            }));
        }
    }, [formData.modelloNinfa]);

    useEffect(() => {
        if (formData.modelloNinfa && formData.categoria) {
            const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
            if (rules) {
                if (formData.spessoreVetro && !rules.glassOptions.includes(formData.spessoreVetro)) {
                    setFormData(prev => ({
                        ...prev,
                        spessoreVetro: '',
                        foriScarico: '',
                        fresaturaScarico: '',
                        piattiScarico: ''
                    }));
                }
                if (rules.drainageHoles.length === 1 && !formData.foriScarico) {
                    setFormData(prev => ({ ...prev, foriScarico: rules.drainageHoles[0] }));
                }
                if (rules.milling.length === 1 && !formData.fresaturaScarico) {
                    setFormData(prev => ({ ...prev, fresaturaScarico: rules.milling[0] }));
                }
                if (rules.plates.length === 1 && !formData.piattiScarico) {
                    setFormData(prev => ({ ...prev, piattiScarico: rules.plates[0] }));
                }
            }
        }
    }, [formData.modelloNinfa, formData.categoria]);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // --- Multi-config helpers ---

    const getTotalConfigCount = () => {
        // configurations array + the current one being edited (if it has a model selected)
        const currentIsNew = activeConfigIndex === null && formData.modelloNinfa;
        return configurations.length + (currentIsNew ? 1 : 0);
    };

    const saveCurrentConfig = () => {
        const productConfig = extractProductConfig(formData);
        // Store images separately in ref
        const images = formData.sezioneTecnicaImages || [];

        if (activeConfigIndex !== null) {
            setConfigurations(prev => {
                const updated = [...prev];
                updated[activeConfigIndex] = { ...productConfig, sezioneTecnicaImages: [] };
                return updated;
            });
            configImagesRef.current[activeConfigIndex] = images;
        } else {
            setConfigurations(prev => [...prev, { ...productConfig, sezioneTecnicaImages: [] }]);
            configImagesRef.current.push(images);
        }
    };

    const handleAddAnotherConfig = () => {
        if (!validateCurrentStep()) return;

        saveCurrentConfig();

        // Reset product fields, keep contact info
        const contactInfo = extractContactInfo(formData);
        setFormData({
            ...DEFAULT_FORM_DATA,
            ...contactInfo,
            ...DEFAULT_PRODUCT_DATA
        });
        setActiveConfigIndex(null);
        setCurrentStep(2);

        Swal.fire({
            title: 'Configurazione salvata!',
            text: 'Puoi ora aggiungere una nuova configurazione prodotto.',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false
        });
    };

    const handleEditConfig = (index) => {
        // Save current work first if we have a model selected
        if (formData.modelloNinfa) {
            saveCurrentConfig();
        }

        const config = configurations[index];
        const contactInfo = extractContactInfo(formData);
        const images = configImagesRef.current[index] || [];

        skipModelResetRef.current = true;
        setFormData({
            ...DEFAULT_FORM_DATA,
            ...contactInfo,
            ...config,
            sezioneTecnicaImages: images
        });
        setActiveConfigIndex(index);
        setCurrentStep(2);
    };

    const handleDeleteConfig = (index) => {
        Swal.fire({
            title: 'Elimina configurazione?',
            text: `Vuoi eliminare la configurazione #${index + 1}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Elimina',
            cancelButtonText: 'Annulla',
            confirmButtonColor: '#dc2626'
        }).then((result) => {
            if (result.isConfirmed) {
                setConfigurations(prev => prev.filter((_, i) => i !== index));
                configImagesRef.current = configImagesRef.current.filter((_, i) => i !== index);

                // If we were editing the deleted config, reset
                if (activeConfigIndex === index) {
                    const contactInfo = extractContactInfo(formData);
                    setFormData({ ...DEFAULT_FORM_DATA, ...contactInfo, ...DEFAULT_PRODUCT_DATA });
                    setActiveConfigIndex(null);
                    setCurrentStep(2);
                } else if (activeConfigIndex !== null && activeConfigIndex > index) {
                    setActiveConfigIndex(prev => prev - 1);
                }
            }
        });
    };

    // --- Assemble all configs for generation ---

    const assembleAllConfigs = () => {
        let allConfigs = [...configurations.map((c, i) => ({
            ...c,
            sezioneTecnicaImages: configImagesRef.current[i] || []
        }))];

        // Include current formData as a config (with its images)
        const currentProduct = extractProductConfig(formData);
        currentProduct.sezioneTecnicaImages = formData.sezioneTecnicaImages || [];
        if (activeConfigIndex !== null) {
            allConfigs[activeConfigIndex] = currentProduct;
        } else if (currentProduct.modelloNinfa) {
            allConfigs.push(currentProduct);
        }

        return allConfigs;
    };

    const isGroupedMode = () => {
        const allConfigs = assembleAllConfigs();
        return allConfigs.length > 1;
    };

    // --- Validation (unchanged logic) ---

    const validateCurrentStep = () => {
        switch (currentStep) {
            case 1:
                if (!formData.nominativi || !formData.indirizzo || !formData.telefono ||
                    !formData.email || !formData.rifCantiere) {
                    Swal.fire({ title: 'Campi Obbligatori', text: 'Compila tutti i campi di contatto', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) {
                    Swal.fire({ title: 'Email Non Valida', text: 'Inserisci un indirizzo email valido', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 2:
                if (!formData.modelloNinfa) {
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona un modello Ninfa', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 3:
                if (!formData.categoria) {
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona una categoria di appartenenza', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 4:
                if (!formData.finituraAlluminio) {
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona una finitura per l\'alluminio', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                if (formData.finituraAlluminio === 'VERNICIATO_CUSTOM' && !formData.ralCustomCode) {
                    Swal.fire({ title: 'Codice RAL Richiesto', text: 'Inserisci il codice RAL personalizzato', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 5:
                if (!formData.spessoreVetro) {
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona uno spessore del vetro', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 6:
                if (!formData.tipoFissaggio) {
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona un tipo di fissaggio', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;

            case 7: {
                if (!formData.foriScarico) {
                    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
                    if (rules && rules.drainageHoles.length === 1) {
                        handleChange('foriScarico', rules.drainageHoles[0]);
                        return true;
                    }
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona un\'opzione per i fori di scarico', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;
            }

            case 8: {
                if (!formData.fresaturaScarico) {
                    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
                    if (rules && rules.milling.length === 1) {
                        handleChange('fresaturaScarico', rules.milling[0]);
                        return true;
                    }
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona un\'opzione per la fresatura', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;
            }

            case 9: {
                if (!formData.piattiScarico) {
                    const rules = getAvailableOptions(formData.modelloNinfa, formData.categoria);
                    if (rules && rules.plates.length === 1) {
                        handleChange('piattiScarico', rules.plates[0]);
                        return true;
                    }
                    Swal.fire({ title: 'Selezione Richiesta', text: 'Seleziona un\'opzione per i piatti di scarico', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;
            }

            case 10: {
                if (!formData.prezzo || formData.prezzo <= 0) {
                    Swal.fire({ title: 'Prezzo Richiesto', text: 'Inserisci un prezzo valido (maggiore di 0)', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                if (!formData.quantitaStimata || formData.quantitaStimata <= 0) {
                    Swal.fire({ title: 'Quantità Richiesta', text: 'Inserisci una quantità stimata valida (maggiore di 0)', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                if (!formData.validitaOfferta) {
                    Swal.fire({ title: 'Validità Offerta Richiesta', text: 'Inserisci la data di validità dell\'offerta', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
                if (!dateRegex.test(formData.validitaOfferta)) {
                    Swal.fire({ title: 'Formato Data Non Valido', text: 'Inserisci la data nel formato gg/mm/aaaa (es: 17/05/2026)', icon: 'warning', confirmButtonText: 'OK' });
                    return false;
                }
                return true;
            }

            case 11:
            case 12:
                return true;

            default:
                return true;
        }
    };

    const handleNext = () => {
        if (validateCurrentStep()) {
            setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
        }
    };

    const handlePrevious = () => {
        setCurrentStep(prev => Math.max(prev - 1, 1));
    };

    // --- PDF Generation ---

    const getCreatorInfo = () => ({
        id: user?.userType === 'agent'
            ? (user?.agent_data?.Salesperson || user?.id?.toString())
            : (user?.id?.toString() || 'unknown'),
        name: user?.userType === 'agent'
            ? (user?.agent_data?.Name || user?.name || 'Agente')
            : (user?.name || user?.username || 'Admin'),
        role: user?.role || 'admin'
    });

    const getRevisionInfo = () => isRevisionMode ? {
        parentId: revisionData.id,
        revision: (revisionData.revision || 1) + 1
    } : {};

    const handleGeneratePDF = async () => {
        setIsGenerating(true);
        try {
            const allConfigs = assembleAllConfigs();
            const contactInfo = extractContactInfo(formData);
            const creatorInfo = getCreatorInfo();
            const revisionInfo = getRevisionInfo();

            let result;
            let formDataForSave;

            if (allConfigs.length > 1) {
                // Grouped mode
                result = await generateGroupedCapitolatoPDF(contactInfo, allConfigs);
                formDataForSave = {
                    isGrouped: true,
                    contactInfo,
                    configurations: allConfigs.map(c => {
                        const copy = { ...c };
                        delete copy.sezioneTecnicaImages;
                        return copy;
                    })
                };
            } else {
                // Single config — backward-compatible flat format
                const singleFormData = { ...contactInfo, ...allConfigs[0] };
                result = await generateCapitolatoPDF(singleFormData);
                formDataForSave = { ...singleFormData };
                delete formDataForSave.sezioneTecnicaImages;
            }

            const saveResult = await api.createCapitolato(
                formDataForSave,
                result.blob,
                result.filename,
                creatorInfo,
                revisionInfo
            );

            const revLabel = isRevisionMode ? ` (REV ${revisionInfo.revision})` : '';
            const capNumber = saveResult?.data?.capitolato_number || '';
            const configLabel = allConfigs.length > 1 ? ` con ${allConfigs.length} configurazioni` : '';

            Swal.fire({
                title: 'PDF Generato e Salvato!',
                html: `Il capitolato <strong>${capNumber}${revLabel}</strong>${configLabel} è stato generato, scaricato e salvato con successo.`,
                icon: 'success',
                confirmButtonText: 'OK'
            });
        } catch (error) {
            console.error('Error generating/saving PDF:', error);
            Swal.fire({
                title: 'Errore',
                text: 'Si è verificato un errore durante la generazione del PDF',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsGenerating(false);
        }
    };

    const handlePreviewPDF = async () => {
        setIsGenerating(true);
        try {
            const allConfigs = assembleAllConfigs();
            const contactInfo = extractContactInfo(formData);

            if (allConfigs.length > 1) {
                await previewGroupedCapitolatoPDF(contactInfo, allConfigs);
            } else {
                const singleFormData = { ...contactInfo, ...allConfigs[0] };
                await previewCapitolatoPDF(singleFormData);
            }
        } catch (error) {
            console.error('Error previewing PDF:', error);
            Swal.fire({
                title: 'Errore',
                text: 'Si è verificato un errore durante l\'anteprima del PDF',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsGenerating(false);
        }
    };

    // --- Render ---

    const renderCurrentSection = () => {
        switch (currentStep) {
            case 1:
                return <HeaderSection formData={formData} onChange={handleChange} disabled={isRevisionMode} />;
            case 2:
                return <NinfaModelSection formData={formData} onChange={handleChange} />;
            case 3:
                return <CategorySection formData={formData} onChange={handleChange} />;
            case 4:
                return <FinishSection formData={formData} onChange={handleChange} />;
            case 5:
                return <GlassSection formData={formData} onChange={handleChange} />;
            case 6:
                return <FixingSection formData={formData} onChange={handleChange} />;
            case 7:
                return <DrainageHolesSection formData={formData} onChange={handleChange} />;
            case 8:
                return <MillingSection formData={formData} onChange={handleChange} />;
            case 9:
                return <PlatesSection formData={formData} onChange={handleChange} />;
            case 10:
                return <PriceSection formData={formData} onChange={handleChange} />;
            case 11:
                return <ImageSelectionSection formData={formData} onChange={handleChange} />;
            case 12:
                return <ReviewSection formData={formData} />;
            default:
                return null;
        }
    };

    const getSectionTitle = () => {
        const titles = [
            'Informazioni di Contatto',
            'Modello Ninfa',
            'Categoria Appartenenza',
            'Finiture Alluminio',
            'Spessore Vetro',
            'Tipo di Fissaggio',
            'Fori di Scarico',
            'Fresatura Scarico Acqua',
            'Piatti Scarico Acqua',
            'Prezzo e Dettagli Offerta',
            'Selezione Immagini',
            'Riepilogo'
        ];
        return titles[currentStep - 1];
    };

    const configLabel = activeConfigIndex !== null
        ? `Configurazione ${activeConfigIndex + 1} di ${configurations.length}`
        : configurations.length > 0
            ? `Nuova configurazione (${configurations.length} salvate)`
            : null;

    if (isGenerating) {
        return (
            <div className="min-h-screen bg-gray-100">
                <Header title="Capitolato" />
                <div className="container mx-auto px-4 py-8 flex flex-col items-center justify-center min-h-[60vh]">
                    <LoadingSpinner />
                    <p className="mt-4 text-gray-600">Generazione PDF in corso...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-red-50">
            <div className="absolute inset-0 bg-[radial-gradient(#1976d2_0.5px,transparent_0.5px)] [background-size:24px_24px] opacity-30" />

            <Header title="Generatore Capitolato" backTo="/dashboard" />

            <div className="relative container mx-auto px-4 py-8 max-w-4xl">
                {/* Revision Banner */}
                {isRevisionMode && (
                    <div className="mb-4 p-4 bg-amber-50 border border-amber-300 rounded-lg flex items-center gap-3">
                        <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        <div>
                            <p className="font-semibold text-amber-800">
                                Modalità Revisione — REV {(revisionData.revision || 1) + 1} del capitolato {revisionData.capitolato_number}
                            </p>
                            <p className="text-sm text-amber-700">
                                I campi di contatto (Step 1) sono bloccati. Modifica le configurazioni necessarie e genera il nuovo PDF.
                            </p>
                        </div>
                    </div>
                )}

                {/* Configuration List (when multi-config) */}
                {configurations.length > 0 && (
                    <ConfigurationList
                        configurations={configurations}
                        activeConfigIndex={activeConfigIndex}
                        onEdit={handleEditConfig}
                        onDelete={handleDeleteConfig}
                    />
                )}

                {/* Config label */}
                {configLabel && (
                    <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-medium text-blue-800">
                            {configLabel}
                        </p>
                    </div>
                )}

                {/* Progress Indicator */}
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                            Passo {currentStep} di {TOTAL_STEPS}
                        </span>
                        <span className="text-sm font-medium text-red-600">
                            {Math.round((currentStep / TOTAL_STEPS) * 100)}%
                        </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                            className="bg-gradient-to-r from-red-500 to-red-600 h-2.5 rounded-full transition-all duration-300"
                            style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                        />
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{getSectionTitle()}</p>
                </div>

                {/* Form Content */}
                <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 mb-6">
                    <StepErrorBoundary stepKey={currentStep}>
                        {renderCurrentSection()}
                    </StepErrorBoundary>
                </div>

                {/* Warning for final step */}
                {currentStep === TOTAL_STEPS && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4 text-center">
                        <p className="text-sm text-yellow-800">
                            Prima di emettere un capitolato, ricontrollare cliccando <strong>Anteprima PDF</strong>.
                            Una volta generato non potrà più essere modificato, né eliminato.
                        </p>
                    </div>
                )}

                {/* Add Another Configuration — separate row above nav */}
                {currentStep === TOTAL_STEPS && (
                    <div className="mb-4">
                        <button
                            type="button"
                            onClick={handleAddAnotherConfig}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-red-400 hover:text-red-600 hover:bg-red-50 transition-all text-sm font-medium"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Aggiungi un'altra configurazione prodotto
                        </button>
                    </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handlePrevious}
                        disabled={currentStep === 1}
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        Indietro
                    </Button>

                    <div className="flex gap-3">
                        {currentStep === TOTAL_STEPS ? (
                            <>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handlePreviewPDF}
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    Anteprima PDF
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    onClick={() => setShowConfirmGenerate(true)}
                                >
                                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Genera PDF
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleNext}
                            >
                                Avanti
                                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            {/* Confirmation Modal for Generate PDF */}
            {showConfirmGenerate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">
                            Conferma generazione capitolato
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                            Hai controllato il documento con <strong>Anteprima PDF</strong>?
                        </p>
                        {isGroupedMode() && (
                            <p className="text-sm text-blue-700 bg-blue-50 rounded-lg p-2 mb-2">
                                Questo capitolato contiene <strong>{assembleAllConfigs().length} configurazioni</strong> che verranno unite in un unico PDF.
                            </p>
                        )}
                        <p className="text-sm text-gray-600 mb-6">
                            Una volta generato, il capitolato <strong>non potrà più essere modificato né eliminato</strong>.
                        </p>
                        <div className="flex justify-end space-x-3">
                            <button
                                onClick={() => setShowConfirmGenerate(false)}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                            >
                                Annulla
                            </button>
                            <button
                                onClick={() => {
                                    setShowConfirmGenerate(false);
                                    handleGeneratePDF();
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Genera capitolato
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CapitolatoForm;
